export interface GenerateFilterExpressionOptions {
    useQPrefix?: boolean;
    keyMap?: Record<string, string>;
    valueMap?: Record<string, Record<string, string | number>>;
}

const ARRAY_DELIMITER = '~';
const EXCLUDED_FILTER_VALUES = new Set([
    'no filter',
    'no filters',
    'all providers',
]);
const URL_STATE_KEYS = new Set([
    'page',
    'pageSize',
    'page_size',
    'sort_field',
    'sort_dir',
    'q.user_filter',
    'user_filter',
]);
const STORED_FILTER_EXCLUDED_KEYS = [
    'q.page',
    'page',
    'q.page_size',
    'page_size',
    'q.sort_field',
    'sort_field',
    'q.sort_dir',
    'sort_dir',
    'q.user_filter',
    'user_filter',
    'q.warningStatus',
    'warningStatus',
    'q.column_set',
    'column_set',
    'q.sf_column_set',
    'sf_column_set',
];

const URL_FILTER_EXCLUDED_KEYS = [
    'q.page',
    'page',
    'q.pageSize',
    'pageSize',
    'q.page_size',
    'page_size',
    'q.sort_field',
    'sort_field',
    'q.sort_dir',
    'sort_dir',
    'q.user_filter',
    'user_filter',
    'q.warningStatus',
    'warningStatus',
    'q.column_set',
    'column_set',
    'q.sf_column_set',
    'sf_column_set',
];

const normalizeExpression = (params: URLSearchParams): string => params.toString().replace(/%7E/gi, ARRAY_DELIMITER);

const isExcludedFilterValue = (value: unknown): boolean => {
    if (typeof value !== 'string') {
        return false;
    }

    const trimmedValue = value.trim();
    return trimmedValue === '' || EXCLUDED_FILTER_VALUES.has(trimmedValue.toLowerCase());
};

const normalizeSerializableValue = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        const normalizedValues = value
            .filter((item) => item !== null && item !== undefined)
            .map((item) => typeof item === 'string' ? item.trim() : item)
            .filter((item) => !isExcludedFilterValue(item));

        return normalizedValues;
    }

    if (typeof value === 'string') {
        // Do NOT trim here — trimming during the draft-state round-trip would
        // strip spaces the user is actively typing (e.g. "Main Street").
        // Trimming for URL / API use happens in sanitizeQueryParamsForNavigation.
        return value;
    }

    return value;
};

const normalizeToQPrefixedExpression = (expression: string): string => {
    const params = new URLSearchParams(expression);
    const result = new URLSearchParams();
    const prefixedKeys = new Set<string>();

    for (const [key] of params.entries()) {
        if (key.startsWith('q.')) {
            prefixedKeys.add(key.substring(2));
        }
    }

    for (const [key, value] of params.entries()) {
        if (key.startsWith('q.')) {
            result.append(key, value);
            continue;
        }

        const prefixedKey = `q.${key}`;
        if (!prefixedKeys.has(key)) {
            result.append(prefixedKey, value);
        }
    }

    return normalizeExpression(result);
};

const hasSerializableValue = (value: unknown): boolean => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return value !== null && value !== undefined && !isExcludedFilterValue(value);
};

const sanitizeNumericParamValue = (value: string): string | null => {
    const digitsOnly = value.replace(/[^\d]/g, '');
    return digitsOnly.length > 0 ? digitsOnly : null;
};

const sanitizeSortFieldValue = (value: string): string | null => {
    const sanitizedValue = value.replace(/[^A-Za-z0-9_.-]/g, '');
    return sanitizedValue.length > 0 ? sanitizedValue : null;
};

const sanitizeSortDirectionValue = (value: string): string | null => {
    const sanitizedValue = value.replace(/[^A-Za-z_-]/g, '');
    return sanitizedValue.length > 0 ? sanitizedValue : null;
};

const sanitizeStateParamValue = (key: string, value: string): string | null => {
    const trimmedValue = value.trim();

    if (trimmedValue === '') {
        return null;
    }

    if (key === 'page' || key === 'pageSize' || key === 'page_size' || key === 'q.user_filter' || key === 'user_filter') {
        return sanitizeNumericParamValue(trimmedValue);
    }

    if (key === 'sort_field') {
        return sanitizeSortFieldValue(trimmedValue);
    }

    if (key === 'sort_dir') {
        return sanitizeSortDirectionValue(trimmedValue);
    }

    return trimmedValue;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateFilterExpression = (
    filters: Record<string, any>,
    options: GenerateFilterExpressionOptions = {}
): string => {
    const params = new URLSearchParams();

    const {
        useQPrefix = true,
        keyMap = {},
        valueMap = {},
    } = options;

    for (const key in filters) {
        if (Object.prototype.hasOwnProperty.call(filters, key)) {
            const value = filters[key];
            const bareKey = keyMap[key] || key;
            const paramKey = useQPrefix ? `q.${bareKey}` : bareKey;
            let finalValue: any = value;

            if (valueMap[key] && valueMap[key][value as string] !== undefined) {
                finalValue = valueMap[key][value as string];
            }

            finalValue = normalizeSerializableValue(finalValue);

            if (hasSerializableValue(finalValue)) {
                if (Array.isArray(finalValue)) {
                    const serialized = finalValue.join(ARRAY_DELIMITER);
                    params.append(paramKey, serialized);
                } else {
                    params.append(paramKey, String(finalValue));
                }
            }
        }
    }

    return normalizeExpression(params);
};

/**
 * Keys that must never appear in a stored filter expression.
 * Covers both q.* (URL form) and bare (storage form) variants.
 */
export const NON_FILTER_KEYS = new Set([
    ...STORED_FILTER_EXCLUDED_KEYS,
]);

export const NON_URL_FILTER_KEYS = new Set([
    ...URL_FILTER_EXCLUDED_KEYS,
]);

export const getInvalidStoredFilterKeys = (expression: string): string[] => {
    const params = new URLSearchParams(expression);
    return [...new Set(
        [...params.keys()].filter((key) => NON_FILTER_KEYS.has(key))
    )];
};

/**
 * Removes pagination, sort, and self-reference keys from a filter expression string.
 * Call this before storing (saveFilterset / updateFilterset) and before applying
 * a saved expression retrieved from the DB (backward safety for legacy records).
 *
 * Handles both URL-form ("q.companies=1") and storage-form ("companies=1") expressions.
 */
export const sanitizeFilterExpression = (expression: string): string => {
    const params = new URLSearchParams(expression);
    const result = new URLSearchParams();
    for (const [key, value] of params.entries()) {
        if (!NON_FILTER_KEYS.has(key) && hasSerializableValue(normalizeSerializableValue(value))) {
            result.append(key, value);
        }
    }
    return normalizeExpression(result);
};

export const sanitizeUrlFilterExpression = (expression: string): string => {
    const params = new URLSearchParams(expression);
    const result = new URLSearchParams();

    for (const [key, value] of params.entries()) {
        if (!NON_URL_FILTER_KEYS.has(key) && hasSerializableValue(normalizeSerializableValue(value))) {
            result.append(key, value);
        }
    }

    return normalizeExpression(result);
};

export const sanitizeQueryParamsForNavigation = (params: URLSearchParams): URLSearchParams => {
    const result = new URLSearchParams();
    const entries = [...params.entries()];
    const prefixedKeys = new Set(
        entries
            .filter(([key]) => key.startsWith('q.'))
            .map(([key]) => key.substring(2))
    );

    entries.forEach(([key, value]) => {
        if (URL_STATE_KEYS.has(key)) {
            const sanitizedValue = sanitizeStateParamValue(key, value);
            if (sanitizedValue !== null) {
                result.set(key, sanitizedValue);
            }
            return;
        }

        if (NON_URL_FILTER_KEYS.has(key)) {
            return;
        }

        if (!key.startsWith('q.') && prefixedKeys.has(key) && !URL_STATE_KEYS.has(key)) {
            return;
        }

        const normalizedValue = normalizeSerializableValue(value);
        if (hasSerializableValue(normalizedValue)) {
            // Trim string filter values when writing to the URL (Apply / navigation).
            // This is the correct point to trim — not during draft-state updates.
            const urlValue = typeof normalizedValue === 'string'
                ? normalizedValue.trim()
                : normalizedValue;
            if (hasSerializableValue(urlValue)) {
                result.append(key, String(urlValue));
            }
        }
    });

    return result;
};

/**
 * Strips the 'q.' prefix from all parameter keys in a query string.
 *
 * Utility retained for backward compatibility with older React-side saved-filter
 * handling that expected bare-key expressions.
 *
 * Current storage normalization preserves q.* keys for RoR compatibility.
 *
 * parseFilterExpression handles both formats for backward compatibility
 * with existing DB records that were saved with the prefix.
 */
export const stripQPrefix = (expression: string): string => {
    const params = new URLSearchParams(expression);
    const result = new URLSearchParams();

    // Collect bare (non-prefixed) keys first so we can skip q.* duplicates.
    const bareKeys = new Set<string>();
    for (const [key] of params.entries()) {
        if (!key.startsWith('q.')) {
            bareKeys.add(key);
        }
    }

    for (const [key, value] of params.entries()) {
        if (key.startsWith('q.')) {
            const bareKey = key.substring(2);
            // If a bare version already exists, skip — avoids duplicate entries in storage.
            // If no bare version exists (old-format expression), convert q.key → key for
            // backward compatibility with stored RoR expressions.
            if (!bareKeys.has(bareKey)) {
                result.append(bareKey, value);
            }
        } else {
            result.append(key, value);
        }
    }

    return normalizeExpression(result);
};

export const toStoredFilterExpression = (value: string | Record<string, any>): string => {
    const rawExpression = typeof value === 'string'
        ? value
        : generateFilterExpression(value);

    // RoR expects q.* keys in stored expressions. Invalid page-state/UI-only keys
    // are stripped first, then any remaining bare keys are normalized to q.*.
    return normalizeToQPrefixedExpression(sanitizeFilterExpression(rawExpression));
};

export const parseBooleanFilterValue = (value: unknown): boolean | null => {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value !== 'string') {
        return null;
    }

    const normalizedValue = value.trim().toLowerCase();

    if (normalizedValue === '1' || normalizedValue === 'true') {
        return true;
    }

    if (normalizedValue === '0' || normalizedValue === 'false') {
        return false;
    }

    return null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseFilterExpression = (expression: string): Record<string, any> => {
    const params = new URLSearchParams(expression);
    const filters: Record<string, any> = {};

    for (const [key, value] of params.entries()) {
        // Remove 'q.' prefix if present
        const cleanKey = key.startsWith('q.') ? key.substring(2) : key;
        // Backward compatibility: prefer an existing bare legacy key over q.*
        // if both appear in older URLs/records.
        if (key.startsWith('q.') && params.has(cleanKey)) {
            continue;
        }
        if (value.includes(ARRAY_DELIMITER)) {
            filters[cleanKey] = value.split(ARRAY_DELIMITER);
        } else if (value.includes(',')) {
            // Backward compatibility for legacy comma-separated filter values
            filters[cleanKey] = value.split(',');
        } else {
            filters[cleanKey] = value;
        }
    }
    return filters;
};
